/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    ICheckoutDeliveryOption,
    ICheckoutDeliveryOptionEdit,
    ICheckoutDeliveryOptionsList,
    ICheckoutDeliveryOptionsViewProps
} from '@msdyn365-commerce-modules/checkout';

import { ICheckoutDeliveryOptionsData } from '../../../dataTypes/checkout-delivery-options.data';
import { ICheckoutDeliveryOptionsProps } from '../definition-extensions/checkout-delivery-options.ext.props.autogenerated';
import { PaymentStateShareInput } from '../../../actions/payment-state-share.action';

const DeliveryOptionList: React.FC<ICheckoutDeliveryOptionsList> = ({ DeliveryOptionsList, list }) => {
    if (!list || list.length === 0) {
        return null;
    }

    return (
        <Node {...DeliveryOptionsList}>
            {list.map((deliveryOption: ICheckoutDeliveryOptionEdit) => {
                return (
                    <Node {...deliveryOption.DeliveryOption} key={deliveryOption.code} aria-label='Please select a delivery option.'>
                        {deliveryOption.radioButton}
                        {deliveryOption.description}
                        {deliveryOption.price}
                    </Node>
                );
            })}
        </Node>
    );
};

const DeliveryOptionSelected: React.FC<ICheckoutDeliveryOption> = ({ DeliveryOption, description, price }) => {
    return (
        <Node {...DeliveryOption}>
            {description}
            {price}
        </Node>
    );
};

const updateCheckoutState = async (
    props: ICheckoutDeliveryOptionsViewProps & ICheckoutDeliveryOptionsProps<ICheckoutDeliveryOptionsData>,
    isShowSelected: boolean
) => {
    const actionContext = props.context.actionContext;
    if (isShowSelected) {
        actionContext.update(new PaymentStateShareInput(), {
            isAccountPaymentSelected: true,
            isCreditCardSelected: false,
            shouldApplyOnAccountPayment: true
        });
    } else {
        actionContext.update(new PaymentStateShareInput(), {
            isAccountPaymentSelected: true,
            isCreditCardSelected: false,
            shouldApplyOnAccountPayment: false
        });
    }
};

const CheckoutDeliveryOptionsView: React.FC<ICheckoutDeliveryOptionsViewProps &
    ICheckoutDeliveryOptionsProps<ICheckoutDeliveryOptionsData>> = props => {
    const {
        CheckoutDeliveryOptions,
        enableCheckoutCartLineDeliveryOptions,
        checkoutErrorRef,
        viewState,
        deliveryOptions,
        deliveryOptionSelected,
        alert,
        waiting,
        saveButton,
        editButton,
        cancelButton,
        checkoutCartLineGroupDeliveryOptions
    } = props;
    React.useEffect(() => {
        updateCheckoutState(props, viewState.isShowSelected);
    }, [viewState.isShowSelected]);
    return (
        // TODO: All wrapper div should be provided by viewProps. Once SDK provide the sln, update markup.
        <Module {...CheckoutDeliveryOptions} ref={checkoutErrorRef}>
            {viewState.isLoading && waiting}
            {viewState.isError && alert}
            {viewState.isShowList && deliveryOptions && !enableCheckoutCartLineDeliveryOptions && (
                <DeliveryOptionList {...deliveryOptions} />
            )}
            {viewState.isShowList && enableCheckoutCartLineDeliveryOptions && checkoutCartLineGroupDeliveryOptions}
            {viewState.isShowSelected && deliveryOptionSelected && <DeliveryOptionSelected {...deliveryOptionSelected} />}
            {viewState.isShowSaveButton && saveButton}
            {viewState.isShowEditButton && editButton}
            {viewState.isShowCancelButton && cancelButton}
        </Module>
    );
};

export default CheckoutDeliveryOptionsView;
